import React, { useEffect, useRef } from "react";
import styles from "./styles.module.scss";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { MediaModel } from "app/models";
import { cls } from "app/utils";

export const VideoJS = (props: any) => {
  const videoNodeRef = useRef(null);
  const playerRef = useRef(null);
  const { options, onReady } = props;
  const isInitializing = useRef(false);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    let player;
    const cleanupFns: Array<() => void> = [];

    if (
      !playerRef.current &&
      videoNodeRef.current &&
      !isInitializing.current &&
      document.contains(videoNodeRef.current)
    ) {
      isInitializing.current = true;

      const mobileOptimizedOptions = {
        ...options,
        playsinline: true,
        preload: "metadata",
        nativeControlsForTouch: true,
        fluid: true,
        responsive: true,
        html5: {
          vhs: {
            enableLowInitialPlaylist: true,
            smoothQualityChange: true,
            overrideNative: false,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
      };

      try {
        player = videojs(videoNodeRef.current, mobileOptimizedOptions);
        playerRef.current = player;
        isInitializing.current = false;
      } catch (error) {
        console.error("Failed to initialize video.js:", error);
        isInitializing.current = false;
        return undefined;
      }

      player.on("error", () => {
        const error = player.error();
        console.error("Video.js error:", error);

        // Clear error and try to recover
        if (error && (error.code === 2 || error.code === 4)) {
          setTimeout(() => {
            if (player && !player.isDisposed()) {
              player.error(null);
              player.load();
            }
          }, 1000);
        }
      });

      player.ready(() => {
        if (onReady) {
          onReady(player);
        }

        const videoEl = player.el()?.querySelector("video");
        if (videoEl) {
          videoEl.setAttribute("playsinline", "true");
          videoEl.setAttribute("webkit-playsinline", "true");

          if (videoEl.requestPictureInPicture) {
            const originalRequestPiP =
              videoEl.requestPictureInPicture.bind(videoEl);
            videoEl.requestPictureInPicture = function() {
              return originalRequestPiP().catch((e: Error) => {
                console.warn("PiP request failed:", e.message);
              });
            };
          }
        }
      });
    } else if (playerRef.current) {
      player = playerRef.current;

      if (options.poster) {
        player.poster(options.poster);
      }

      try {
        player.error(null);
        player.src(options.sources);
        player.load();
      } catch (error) {
        console.error("Error updating video source:", error);
      }
    }

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, [options.sources, options.poster, onReady]);

  useEffect(() => {
    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div className={cls(styles.video_player)}>
      <div data-vjs-player>
        <video
          ref={videoNodeRef}
          className="video-js vjs-big-play-centered"
          playsInline
          webkit-playsinline="true"
        />
      </div>
    </div>
  );
};

const VideoPlayer = ({
  src,
  onMetadataLoaded,
}: {
  src: MediaModel;
  onMetadataLoaded?: (videoWidth: number, videoHeight: number) => void;
}) => {
  const posterUrl = src?.thumbnails?.default;
  const videoUrl = src?.versions?.default?.url ?? src?.originalUrl;

  const videoJsOptions = {
    autoplay: false,
    controls: true,
    responsive: true,
    fluid: true,
    playsinline: true,
    preload: "metadata",
    poster: posterUrl,
    sources: videoUrl
      ? [
          {
            src: videoUrl,
            type: "video/mp4",
          },
        ]
      : [],
    nativeControlsForTouch: true,
    html5: {
      vhs: {
        enableLowInitialPlaylist: true,
        smoothQualityChange: true,
        overrideNative: false,
      },
      nativeAudioTracks: false,
      nativeVideoTracks: false,
    },
    controlBar: {
      pictureInPictureToggle: false,
    },
  };

  const handlePlayerReady = (player: any) => {
    const videoElement = player.el()?.querySelector("video");
    if (videoElement) {
      videoElement.setAttribute("playsinline", "true");
      videoElement.setAttribute("webkit-playsinline", "true");

      if (posterUrl) {
        videoElement.setAttribute("poster", posterUrl);
      }

      const handleLoadedMetadata = () => {
        try {
          const width = videoElement.videoWidth;
          const height = videoElement.videoHeight;

          if (width && height && onMetadataLoaded) {
            onMetadataLoaded(width, height);
          }
        } catch (error) {
          console.error("Error getting video dimensions:", error);
        }
      };

      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    }
  };

  if (!videoUrl) {
    console.error("No video URL provided");
    return <div>No video source available</div>;
  }

  return <VideoJS options={videoJsOptions} onReady={handlePlayerReady} />;
};

export default VideoPlayer;
